
import React, { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import "../Style/Alerts.css";

// --- Constants ---
const API_BASE_URL = "https://api.yellowmatrix.co.in";

// --- Initial Data for Alert Cards ---
const initialAlertData = [
  {
    id: 1,
    src: "/Alerts_icon/Overspeed.png",
    alt: "Overspeed",
    label: "Overspeed",
    count: 0,
    status: "inactive",
  },
  {
    id: 2,
    src: "/Alerts_icon/Route Fence Breach IN OUT.png",
    alt: "Route Fence",
    label: "Drive Alert",
    count: 0,
    status: "inactive",
  },
  {
    id: 3,
    src: "/Alerts_icon/Fall.png",
    alt: "Fall",
    label: "Fall Alert",
    count: 0,
    status: "inactive",
  },
  {
    id: 4,
    src: "/Alerts_icon/No Response.png",
    alt: "No Response",
    label: "Tow Alert",
    count: 0,
    status: "inactive",
  },
  {
    id: 5,
    src: "/Alerts_icon/Device Pullout.png",
    alt: "Device Pullout",
    label: "Device Unplugged",
    count: 0,
    status: "inactive",
  },
  {
    id: 6,
    src: "/Alerts_icon/AOI Geofence Breach.png",
    alt: "Geofence",
    label: "Immobilize",
    count: 0,
    status: "inactive",
  },
  {
    id: 7,
    src: "/Alerts_icon/Idle Alert.png",
    alt: "Idle",
    label: "Panic Button",
    count: 0,
    status: "inactive",
  },
  {
    id: 8,
    src: "/Alerts_icon/Halt Alert.png",
    alt: "Halt Alert",
    label: "Device Tamper",
    count: 0,
    status: "inactive",
  },
  {
    id: 9,
    src: "/Alerts_icon/Schedule Maintenance.png",
    alt: "Schedule",
    label: "Schedule Maintenance",
    count: 0,
    status: "inactive",
  },
  {
    id: 10,
    src: "/Alerts_icon/maintenance.png",
    alt: "Conditional",
    label: "Conditional Maintenance",
    count: 0,
    status: "inactive",
  },
  {
    id: 11,
    src: "/Alerts_icon/motor.png",
    alt: "Motor Controller",
    label: "Motor Controller",
    count: 0,
    status: "inactive",
  },
  {
    id: 12,
    src: "/Alerts_icon/Device Battery Low.png",
    alt: "Battery",
    label: "Custom Alerts",
    count: 0,
    status: "inactive",
  },
  {
    id: 13,
    src: "/Alerts_icon/Geofence.png",
    alt: "Geofence",
    label: "Geofence",
    count: 0,
    status: "inactive",
  },
  {
    id: 14,
    src: "/Alerts_icon/Long Idel.png",
    alt: "Ideling",
    label: "Long Ideling",
    count: 0,
    status: "inactive",
  },
  {
    id: 15,
    src: "/Alerts_icon/Malfunction.png",
    alt: "Malfunction",
    label: "Malfunction",
    count: 0,
    status: "inactive",
  },
];

const CREATE_ALERT_OPTIONS = [
  { label: "Overspeed Alert", type: "overspeed" },
  { label: "Harsh Breaking Alert", type: "harsh_breaking" },
  { label: "Harsh Acceleration Alert", type: "harsh_acceleration" },
  { label: "Sharp Turn Alert", type: "sharp_turn" },
  { label: "Long Idling Alert", type: "long_idling" },
  { label: "Geofence Alert", type: "geofence" },
  { label: "Panic Button Alert", type: "panic_button" },
  { label: "Device Tamper Alert", type: "device_tamper" },
  { label: "Device Unplugged Alert", type: "device_unplugged" },
  { label: "DTC Alert", type: "dtc_alert" },
  { label: "Custom Alerts (Analog)", type: "custom_alerts" },
];

const ALERT_INPUT_SCHEMA = {
  overspeed: { field: "overspeed_threshold", label: "Threshold Value" },
  harsh_breaking: {
    field: "harsh_breaking_threshold",
    label: "Threshold Value",
  },
  harsh_acceleration: {
    field: "harsh_acceleration_threshold",
    label: "Threshold Value",
  },
  sharp_turn: { field: "sharp_turn_threshold", label: "Threshold Value" },
  long_idling: { field: "long_idling_minutes", label: "Minutes" },
};

const ALERT_FIELD_MAPPING = {
  overspeed: "overspeed",
  harsh_breaking: "harsh_breaking",
  harsh_acceleration: "harsh_acceleration",
  sharp_turn: "sharp_turn",
  long_idling: "long_idling",
  geofence: "geofence",
  panic_button: "panic_button",
  device_tamper: "device_tamper",
  device_unplugged: "device_unplugged",
  dtc_alert: "dtc_alert",
};

const ALERT_DETAIL_ENDPOINTS = {
  Overspeed: "/api/alerts/overspeed",
  "Drive Alert": "/api/alerts/drive",
  "Fall Alert": "/api/alerts/fall",
  "Panic Button": "/api/alerts/panic",
  "Device Tamper": "/api/alerts/tamper",
  "Device Unplugged": "/api/alerts/unplugged",
  Geofence: "/api/alerts/geofence",
  "Long Ideling": "/api/alerts/idle",
  "Custom Alerts": "/api/alerts/analog",
  Malfunction: "/api/dtc",
};

// --- Utility: API Call ---
const makeApiCall = async (url, method = "GET", body = null) => {
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
  try {
    const response = await fetch(url, {
      method,
      headers,
      body: body ? JSON.stringify(body) : null,
    });
    const responseBody = await response.json().catch(() => ({}));
    if (!response.ok) {
      return {
        success: false,
        error:
          responseBody.error ||
          responseBody.message ||
          `HTTP error! Status: ${response.status}`,
        status: response.status,
      };
    }
    return { success: true, data: responseBody, status: response.status };
  } catch (err) {
    console.error("API Call failed:", err);
    return { success: false, error: "Network or application error." };
  }
};

// --- Utility: Build URL with timeframe ---
const buildUrl = (base, vehicleId, range, fromDate, toDate, extra = "") => {
  const isAll = vehicleId === "ALL";
  let url = isAll
    ? `${API_BASE_URL}${base}?`
    : `${API_BASE_URL}${base}?vehicleid=${vehicleId}&`;

  if (range === "Custom") {
    url += `timeframe=custom&from_date=${fromDate}&to_date=${toDate}`;
  } else {
    let timeframe = "today";
    if (range === "Last Week") timeframe = "last_7_days";
    else if (range === "This Month") timeframe = "last_30_days";
    url += `timeframe=${timeframe}`;
  }
  if (extra) url += extra;
  return url;
};

// --- Custom Hook 1: Fetch Alert Counts ---
const useAlertCountsQuery = (vehicleId, range, fromDate, toDate) => {
  const fetchCounts = async () => {
    if (!vehicleId) return {};

    const endpoints = {
      Overspeed: "/api/alerts/overspeed/count",
      "Drive Alert": "/api/alerts/drive/count",
      "Fall Alert": "/api/alerts/fall/count",
      "Panic Button": "/api/alerts/panic/count",
      "Device Tamper": "/api/alerts/tamper/count",
      "Device Unplugged": "/api/alerts/unplugged/count",
      Geofence: "/api/alerts/geofence/count",
      "Long Ideling": "/api/alerts/idle/count",
      "Custom Alerts": "/api/alerts/analog/count",
      Malfunction: "/api/dtc/count",
    };

    const results = await Promise.all(
      Object.entries(endpoints).map(async ([label, endpoint]) => {
        const url = buildUrl(endpoint, vehicleId, range, fromDate, toDate);
        const result = await makeApiCall(url);
        return { label, count: result.success ? result.data.count || 0 : 0 };
      }),
    );

    const formattedCounts = {};
    results.forEach((item) => {
      formattedCounts[item.label] = item.count;
    });
    return formattedCounts;
  };

  return useQuery({
    queryKey: ["alertCounts", vehicleId, range, fromDate, toDate],
    queryFn: fetchCounts,
    enabled: !!vehicleId,
    staleTime: 60000,
    refetchOnWindowFocus: false,
  });
};

// --- Custom Hook 2: Fetch Vehicle List ---
const useVehicleListQuery = () => {
  const fetchVehicles = async () => {
    const result = await makeApiCall(`${API_BASE_URL}/vehicle/device/assign`);
    if (!result.success || !result.data.Vehicles) {
      throw new Error(result.error || "Failed to load vehicles.");
    }
    return result.data.Vehicles;
  };
  return useQuery({
    queryKey: ["vehicleList"],
    queryFn: fetchVehicles,
    staleTime: 5 * 60 * 1000,
  });
};

// --- Component: Dashboard View ---
const DashboardView = ({
  alertData,
  onSelectAlert,
  onOpenConfig,
  selectedOption,
  setSelectedOption,
  searchQuery,
  setSearchQuery,
  loadingCounts,
  vehicleListQuery,
  fromDate,
  toDate,
  setFromDate,
  setToDate,
  showDatePopup,
  setShowDatePopup,
  handleApplyCustomDate,
}) => {
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const statusColors = { active: "#fd4416ff" };
  const {
    data: vehicles = [],
    isLoading: loadingVehicles,
    error: vehicleError,
  } = vehicleListQuery;

  const toggleDropdown = (e) => {
    e.stopPropagation();
    setDropdownOpen((prev) => !prev);
  };

  const selectOption = (option) => {
    setDropdownOpen(false);
    if (option === "Custom") {
      setShowDatePopup(true);
      return;
    }
    setSelectedOption(option);
  };

  useEffect(() => {
    const closeDropdown = () => setDropdownOpen(false);
    if (isDropdownOpen) window.addEventListener("click", closeDropdown);
    return () => window.removeEventListener("click", closeDropdown);
  }, [isDropdownOpen]);

  const addImageFallback = (event) => {
    event.currentTarget.src = "https://placehold.co/40x40/cccccc/FFFFFF?text=X";
  };
  const cardDisabled = loadingCounts || !searchQuery;

  const SkeletonCard = () => (
    <div className="alert-card skeleton-card-Alerts">
      <div className="skeleton-image-Alerts" />
      <br />
      <div className="skeleton-text-Alerts" />
    </div>
  );

  const renderAlertCards = () => {
    if (loadingCounts && searchQuery) {
      return initialAlertData.map((alert) => <SkeletonCard key={alert.id} />);
    }
    return alertData.map((alert) => (
      <div
        key={alert.id}
        className={`alert-card ${cardDisabled ? "disabled" : ""}`}
        style={{
          borderColor:
            alert.count > 0 ? statusColors.active : "var(--border-color)",
        }}
        onClick={() => (cardDisabled ? null : onSelectAlert(alert))}
        title={
          cardDisabled
            ? searchQuery
              ? "Loading counts..."
              : "Select a Vehicle to view details"
            : ""
        }
      >
        {alert.count > 0 && (
          <div
            className="alert-notification-badge"
            style={{ background: statusColors.active }}
          >
            {alert.count > 99 ? "99+" : alert.count}
          </div>
        )}
        <img src={alert.src} alt={alert.alt} onError={addImageFallback} />
        <br />
        {alert.label}
      </div>
    ));
  };

  return (
    <div className="Alerts-grid-container">
      <h2 id="Alert-H2">Alerts Dashboard</h2>
      <p className="subtitle-alert">
        Stay informed with instant alerts on vehicle status and critical events.
      </p>
      <hr className="line-alert" />

      <div className="alert-legend">
        <div className="legend-item">
          <span
            className="legend-dot"
            style={{ backgroundColor: statusColors.active }}
          ></span>
          Active
        </div>
      </div>

      <div className="Alerts-top-bar">
        <div className="Alerts-top-bar-left">
          <div className="Alerts-search-container">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M10,18c1.846,0,3.543-0.635,4.897-1.688l4.396,4.396l1.414-1.414l-4.396-4.396C17.365,13.543,18,11.846,18,10 c0-4.411-3.589-8-8-8s-8,3.589-8,8S5.589,18,10,18z M10,4c3.309,0,6,2.691,6,6s-2.691,6-6,6s-6-2.691-6-6S6.691,4,10,4z"></path>
            </svg>
            <select
              id="vehicleSearch"
              className="Alerts-vehicle-dropdown"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              disabled={loadingVehicles || vehicles.length === 0}
            >
              {loadingVehicles ? (
                <option value="" disabled>
                  Loading Vehicles...
                </option>
              ) : vehicleError ? (
                <option value="" disabled>
                  Error Loading Vehicles
                </option>
              ) : vehicles.length === 0 ? (
                <option value="" disabled>
                  No Vehicles Available
                </option>
              ) : (
                <>
                  <option value="ALL">All Vehicles</option>
                  {vehicles.map((vehicle) => (
                    <option key={vehicle.VehicleID} value={vehicle.VehicleID}>
                      {vehicle.VehicleID}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>

          <div className={`dropdown-Alerts ${isDropdownOpen ? "open" : ""}`}>
            <button className="dropdown-button-Alerts" onClick={toggleDropdown}>
              {selectedOption}
            </button>
            {isDropdownOpen && (
              <div className="dropdown-menu-Alerts">
                {["Today", "Last Week", "This Month", "Custom"].map(
                  (option) => (
                    <div key={option} onClick={() => selectOption(option)}>
                      {option}
                    </div>
                  ),
                )}
              </div>
            )}
            {showDatePopup && (
              <div className="date-popup-overlay">
                <div className="date-popup-box">
                  <h3>Select Date Range</h3>
                  <div className="date-input-group">
                    <label>From Date</label>
                    <input
                      type="date"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                    />
                  </div>
                  <div className="date-input-group">
                    <label>To Date</label>
                    <input
                      type="date"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                    />
                  </div>
                  <div className="date-popup-buttons">
                    <button
                      className="cancel-btn"
                      onClick={() => setShowDatePopup(false)}
                    >
                      Cancel
                    </button>
                    <button
                      className="apply-btn"
                      onClick={handleApplyCustomDate}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="Alerts-top-bar-right">
          <button className="config-button-Alerts" onClick={onOpenConfig}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              width="18"
              height="18"
            >
              <path d="M19.479,10.092c-0.262-0.58-0.664-1.096-1.176-1.529l1.107-1.782c0.126-0.203,0.111-0.463-0.04-0.65l-1.414-1.414 c-0.188-0.188-0.447-0.224-0.65-0.04l-1.782,1.107c-0.433-0.512-0.949-0.913-1.529-1.176 L13.908,2.521 c-0.057-0.227-0.279-0.386-0.521-0.386h-2.774c-0.242,0-0.464,0.159-0.521,0.386l-0.086,2.084c-0.58,0.262-1.096,0.664-1.529,1.176 L6.7,4.675C6.5,4.55,6.237,4.565,6.05,4.752L4.636,6.166C4.448,6.354,4.413,6.613,4.57,6.816l1.107,1.782 c-0.512,0.433-0.913,0.949-1.176,1.529L2.521,10.092C2.464,10.319,2.305,10.54,2.305,10.782v2.774 c0,0.242,0.159,0.464,0.386,0.521l2.084,0.086c0.262,0.58,0.664,1.096,1.176,1.529l-1.107,1.782 c-0.126,0.203-0.111,0.463-0.04,0.65l1.414,1.414c0.188,0.188,0.447,0.224,0.65,0.04l1.782-1.107 c0.433,0.512,0.949,0.913,1.529,1.176l0.086,2.084c0.057,0.227,0.279,0.386,0.521,0.386h2.774c0.242,0,0.464,0.159,0.521,0.386 l0.086-2.084c0.58-0.262,1.096-0.664,1.529-1.176l1.782,1.107c0.203,0.126,0.463,0.111,0.65-0.04l1.414-1.414 c0.188-0.188,0.224-0.447,0.04-0.65l-1.107-1.782c0.512-0.433,0.913-0.949,1.176-1.529l2.084-0.086 c0.227-0.057,0.386-0.279,0.386-0.521v-2.774C21.695,10.54,21.536,10.319,21.479,10.092z M11.667,15.667 c-2.206,0-4-1.794-4-4s1.794-4,4-4s4,1.794,4,4S13.872,15.667,11.667,15.667z"></path>
            </svg>
            <span>Configurations</span>
          </button>
        </div>
      </div>

      <div className="alerts">{renderAlertCards()}</div>
      {!searchQuery && (
        <p className="vehicle-id-warning">
          *** Please select a Vehicle or All Vehicles to load alert details. ***
        </p>
      )}
      {vehicleError && (
        <p className="error-message-Alerts">
          Error fetching vehicles: {vehicleError.message}
        </p>
      )}
    </div>
  );
};

// --- Component: Details Panel ---
const DetailsPanel = ({
  alert,
  vehicleId,
  range,
  fromDate,
  toDate,
  onClose,
}) => {
  const [logs, setLogs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [message, setMessage] = React.useState("");
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  const isAll = vehicleId === "ALL";
  const perPage = 5;

  React.useEffect(() => {
    setCurrentPage(1);
  }, [vehicleId, alert.label, range]);

  React.useEffect(() => {
    if (!vehicleId) return;

    const fetchLogs = async () => {
      setLoading(true);
      setMessage("");
      setLogs([]);
      const endpoint = ALERT_DETAIL_ENDPOINTS[alert.label];

      if (!endpoint) {
        setMessage("Invalid alert type selected");
        setLoading(false);
        return;
      }
      if (range === "Custom" && (!fromDate || !toDate)) {
        setMessage("Please select From Date and To Date");
        setLoading(false);
        return;
      }

      let url = isAll
        ? `${API_BASE_URL}${endpoint}?page=${currentPage}&per_page=${perPage}`
        : `${API_BASE_URL}${endpoint}?vehicleid=${vehicleId}&page=${currentPage}&per_page=${perPage}`;
      if (range === "Custom") {
        url += `&timeframe=custom&from_date=${fromDate}&to_date=${toDate}`;
      } else {
        let timeframe = "today";
        if (range === "Last Week") timeframe = "last_7_days";
        else if (range === "This Month") timeframe = "last_30_days";
        url += `&timeframe=${timeframe}`;
      }

      const result = await makeApiCall(url);
      if (result.success) {
        const items = result.data.data || [];
        if (items.length > 0) {
          setLogs(items);
          setHasPrevPage(currentPage > 1);
          setHasNextPage(currentPage < result.data.pages);
        } else {
          setLogs([]);
          setMessage(`No Alerts Details Found for ${range}`);
          setHasNextPage(false);
          setHasPrevPage(currentPage > 1);
        }
      } else {
        setLogs([]);
        setMessage(result.error || "Failed to load alert logs.");
        setHasNextPage(false);
        setHasPrevPage(currentPage > 1);
      }
      setLoading(false);
    };
    fetchLogs();
  }, [vehicleId, currentPage, alert.label, range, fromDate, toDate, isAll]);

  const handleNextPage = () => setCurrentPage((prev) => prev + 1);
  const handlePrevPage = () => setCurrentPage((prev) => Math.max(1, prev - 1));
  const isDtcSelected = alert.label === "Malfunction";

  return (
    <div className="Alerts-details-panel">
      <div className="details-header-Alerts">
        <h3>
          {alert.label} Details ({range}): {isAll ? "All Vehicles" : vehicleId}
        </h3>
        <button className="icon-button-Alerts" onClick={onClose} title="Close">
          &times;
        </button>
      </div>

      <div className="details-table-wrapper-Alerts">
        <table className="details-table-Alerts">
          <thead>
            <tr>
              <th>Vehicle</th>
              <th>Date</th>
              <th>Alert Type</th>
              <th>Threshold Value</th>
              {!isDtcSelected && <th>Exceeded Value</th>}
              <th>Location</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={isDtcSelected ? 5 : 6}
                  style={{ textAlign: "center", padding: "15px" }}
                >
                  Loading alert logs...
                </td>
              </tr>
            ) : logs.length > 0 ? (
              logs.map((item) => {
                const isDTC = alert.label === "Malfunction";
                const dtcCodesArr = Array.isArray(item.dtc_codes)
                  ? item.dtc_codes
                  : typeof item.dtc_codes === "string"
                    ? item.dtc_codes
                        .split(",")
                        .map((c) => c.trim())
                        .filter(Boolean)
                    : [];
                const safeVal = (v) =>
                  !v || v === "null" || v === "None" || v === null ? "-" : v;
                return (
                  <tr key={item.id}>
                    <td>{safeVal(item.vehicleid)}</td>
                    <td>
                      {item.arrivaltime || item.timestamp
                        ? new Date(
                            item.arrivaltime || item.timestamp,
                          ).toLocaleString()
                        : "-"}
                    </td>
                    <td>
                      {isDTC
                        ? dtcCodesArr.length > 0
                          ? dtcCodesArr.join(", ")
                          : "-"
                        : safeVal(item.alert_type)}
                    </td>
                    <td>{isDTC ? item.dtc_count : safeVal(item.threshold)}</td>
                    {!isDTC && <td>{safeVal(item.value)}</td>}
                    <td>
                      {item.latitude &&
                      item.longitude &&
                      String(item.latitude) !== "null" &&
                      String(item.longitude) !== "null"
                        ? `${item.latitude}, ${item.longitude}`
                        : "-"}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={isDtcSelected ? 5 : 6}
                  style={{
                    textAlign: "center",
                    padding: "20px",
                    color: "#666",
                    fontWeight: "bold",
                  }}
                >
                  {message || `No Alerts Details Found for ${range}`}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="pagination-Alerts">
        <button onClick={handlePrevPage} disabled={!hasPrevPage || loading}>
          Previous
        </button>
        <span className="page-info-Alerts">Page {currentPage}</span>
        <button onClick={handleNextPage} disabled={!hasNextPage || loading}>
          Next
        </button>
      </div>
    </div>
  );
};

// --- Component: Configuration View ---
const ConfigurationView = ({ onClose, alertOptions }) => {
  const [selectedConfigAlert, setSelectedConfigAlert] = React.useState(
    alertOptions[0]?.type || "",
  );
  const [customers, setCustomers] = React.useState([]);
  const [selectedCustomer, setSelectedCustomer] = React.useState("");
  const [vehicles, setVehicles] = React.useState([]);
  const [selectedVehicle, setSelectedVehicle] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [alertMessage, setAlertMessage] = React.useState("");
  const [showRetry, setShowRetry] = React.useState(false);

  // NEW: State to store the entire fetched rule for the active vehicle
  const [currentVehicleRule, setCurrentVehicleRule] = React.useState(null);

  const [analogInput1Enabled, setAnalogInput1Enabled] = React.useState(false);
  const [analogInput1Threshold, setAnalogInput1Threshold] = React.useState("");
  const [analogInput2Enabled, setAnalogInput2Enabled] = React.useState(false);
  const [analogInput2Threshold, setAnalogInput2Threshold] = React.useState("");
  const [ruleExists, setRuleExists] = React.useState(false);
  const [alertEnabled, setAlertEnabled] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const schema = ALERT_INPUT_SCHEMA?.[selectedConfigAlert];
  const showInput = Boolean(schema);
  const analogOptions = React.useMemo(
    () => ["10", "25", "40", "55", "75", "85", "100"],
    [],
  );

  // Fetch Customers
  React.useEffect(() => {
    const fetchCustomers = async () => {
      setLoading(true);
      const result = await makeApiCall(`${API_BASE_URL}/customers/list`);
      if (result.success) {
        const customerList = result.data.Customers || [];
        setCustomers(customerList);
        if (customerList.length > 0)
          setSelectedCustomer(customerList[0].CustomerID);
      } else setError(result.error);
      setLoading(false);
    };
    fetchCustomers();
  }, []);

  // Fetch Vehicles
  React.useEffect(() => {
    if (!selectedCustomer) {
      setVehicles([]);
      setSelectedVehicle("");
      return;
    }
    const fetchVehicles = async () => {
      const result = await makeApiCall(
        `${API_BASE_URL}/vehicle/device/assign?customer_id=${selectedCustomer}`,
      );
      if (result.success && result.data.Vehicles) {
        setVehicles(result.data.Vehicles);
        if (result.data.Vehicles.length > 0)
          setSelectedVehicle(result.data.Vehicles[0].VehicleID);
        else setSelectedVehicle("");
      } else {
        setVehicles([]);
        setSelectedVehicle("");
      }
    };
    fetchVehicles();
  }, [selectedCustomer]);

  React.useEffect(() => {
    if (!selectedVehicle) {
      setCurrentVehicleRule(null);
      setRuleExists(false);
      return;
    }
    const fetchAlertRule = async () => {
      const result = await makeApiCall(
        `${API_BASE_URL}/api/alerts/rule?vehicleid=${selectedVehicle}`,
      );
      if (result.success) {
        setCurrentVehicleRule(result.data);
        setRuleExists(true);
      } else {
        setCurrentVehicleRule(null);
        setRuleExists(false);
      }
    };
    fetchAlertRule();
  }, [selectedVehicle]);


  React.useEffect(() => {
    const rule = currentVehicleRule || {};
    const fieldName = ALERT_FIELD_MAPPING[selectedConfigAlert];

    setAlertEnabled(rule[fieldName] || false);

    const currentSchema = ALERT_INPUT_SCHEMA?.[selectedConfigAlert];
    if (
      currentSchema &&
      rule[currentSchema.field] !== undefined &&
      rule[currentSchema.field] !== null
    ) {
      setInputValue(rule[currentSchema.field].toString());
    } else {
      setInputValue("");
    }

    setAnalogInput1Enabled(rule.analog_1 || false);
    setAnalogInput1Threshold(
      rule.analog_1_threshold?.toString() || analogOptions[0],
    );
    setAnalogInput2Enabled(rule.analog_2 || false);
    setAnalogInput2Threshold(
      rule.analog_2_threshold?.toString() || analogOptions[0],
    );
  }, [selectedConfigAlert, currentVehicleRule, analogOptions]);

  const handleToggleAlert = async (enable) => {
    if (!selectedVehicle) {
      setAlertMessage("Please select vehicle");
      return;
    }
    const fieldName = ALERT_FIELD_MAPPING[selectedConfigAlert];
    if (!fieldName) {
      setAlertMessage("Invalid alert selected");
      return;
    }

    const body = { vehicleid: selectedVehicle, [fieldName]: enable };
    if (schema && inputValue) {
      body[schema.field] = Number(inputValue);
    }

    const method = ruleExists ? "PATCH" : "POST";
    const result = await makeApiCall(
      `${API_BASE_URL}/api/alerts/rule`,
      method,
      body,
    );

    if (result.success) {
      setAlertEnabled(enable);
      setShowRetry(false);

      setAlertMessage(
        `${selectedConfigAlert} ${enable ? "Enabled" : "Disabled"} Successfully`,
      );

      setRuleExists(true);
      setCurrentVehicleRule((prev) => ({ ...prev, ...body }));
    } else {
      if (selectedConfigAlert === "panic_button" && result.status === 504) {
        setShowRetry(true);
        setAlertMessage("Please Retry");
      } else {
        setAlertMessage(result.error || "Failed to update alert");
      }
    }
  };

  const handleSaveConfiguration = async (enable, analogField) => {
    if (!selectedVehicle || !selectedCustomer) {
      setAlertMessage("Please select a customer and vehicle first.");
      return;
    }

    let body = { vehicleid: selectedVehicle, customerid: selectedCustomer };
    const newAnalog1Enabled = analogField === 1 ? enable : analogInput1Enabled;
    const newAnalog2Enabled = analogField === 2 ? enable : analogInput2Enabled;

    body.analog_1 = newAnalog1Enabled;
    body.analog_1_threshold = analogInput1Threshold;
    body.analog_2 = newAnalog2Enabled;
    body.analog_2_threshold = analogInput2Threshold;

    let result;
    if (ruleExists) {
      const patchBody = {
        vehicleid: selectedVehicle,
        analog_1: body.analog_1,
        analog_1_threshold: body.analog_1_threshold,
        analog_2: body.analog_2,
        analog_2_threshold: body.analog_2_threshold,
      };
      result = await makeApiCall(
        `${API_BASE_URL}/api/alerts/rule`,
        "PATCH",
        patchBody,
      );
      if (result.success) {
        setAlertMessage(
          result.data.message || "Alert rule updated successfully.",
        );
        setCurrentVehicleRule((prev) => ({ ...prev, ...patchBody }));
      } else if (result.status !== 404 && result.status !== 400) {
        setAlertMessage(`Failed to update rule: ${result.error}`);
        return;
      }
    }

    if (!ruleExists || (result && !result.success)) {
      const postBody = {
        ...body,
        overspeed: false,
        overspeed_threshold: 0,
        harsh_breaking: false,
        harsh_breaking_threshold: 0,
        harsh_acceleration: false,
        harsh_acceleration_threshold: 0,
      };
      result = await makeApiCall(
        `${API_BASE_URL}/api/alerts/rule`,
        "POST",
        postBody,
      );
      if (result.success) {
        setAlertMessage(
          result.data.message || "New alert rule created successfully.",
        );
        setRuleExists(true);
        setCurrentVehicleRule((prev) => ({ ...prev, ...postBody }));
      } else if (
        result.status === 400 &&
        result.error.includes("Already Exists")
      ) {
        setAlertMessage(`Error: Rule already exists. Refresh needed.`);
        return;
      } else {
        setAlertMessage(`Failed to create rule: ${result.error}`);
        return;
      }
    }
    if (result && result.success) {
      if (analogField === 1) setAnalogInput1Enabled(newAnalog1Enabled);
      else if (analogField === 2) setAnalogInput2Enabled(newAnalog2Enabled);
    }
  };

  if (loading)
    return (
      <div className="Config-view-Alerts">
        <p>Loading configurations...</p>
      </div>
    );
  if (error)
    return (
      <div className="Config-view-Alerts">
        <p className="error-message-Alerts">{error}</p>
      </div>
    );

  // Extract the true backend saved value directly for display
  const getSavedThreshold = () => {
    if (!currentVehicleRule || !schema) return "Not Set";
    const val = currentVehicleRule[schema.field];
    return val !== undefined && val !== null ? val : "Not Set";
  };

  return (
    <div className="Config-view-Alerts">
      <div className="config-header-Alerts">
        <div className="config-header-left-Alerts">
          <button className="back-button-Alerts" onClick={onClose}>
            <span>&larr;</span> Back to Alert
          </button>
          <h3 className="config-title-Alerts">Configure Alerts</h3>
        </div>
      </div>

      {alertMessage && (
        <div
          className={`alert-status-message-Alerts ${alertMessage.includes("Error") || alertMessage.includes("Failed") ? "error" : "success"}`}
        >
          {alertMessage}
        </div>
      )}

      <div className="config-controls-Alerts lineofbottom-Alerts">
        <div className="config-select-group-Alerts">
          <label htmlFor="alert-select-Alerts">Select Alert *</label>
          <select
            id="alert-select-Alerts"
            className="config-select-Alerts"
            value={selectedConfigAlert}
            onChange={(e) => {
              setSelectedConfigAlert(e.target.value);
            }}
          >
            {alertOptions.map((alert) => (
              <option key={alert.type} value={alert.type}>
                {alert.label}
              </option>
            ))}
          </select>

          {showInput && selectedConfigAlert !== "custom_alerts" && (
            <div className="threshold-input-group">
              <label htmlFor="threshold-input-Alerts">{schema.label}</label>
              <input
                id="threshold-input-Alerts"
                type="number"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder={`Enter ${schema.label}`}
                className="config-input-Alerts"
              />
            </div>
          )}

          {selectedConfigAlert !== "custom_alerts" && (
            <>
              <div className="button-container-Alerts single-line">
                {/* Show ONLY Disable for panic_button */}
                {selectedConfigAlert === "panic_button" ? (
                  <button
                    className="disable-button-Alerts"
                    onClick={() => handleToggleAlert(false)}
                    disabled={!selectedVehicle}
                  >
                    {showRetry ? "Retry" : "Disable"}
                  </button>
                ) : (
                  <>
                    <button
                      className="enable-button-Alerts"
                      onClick={() => handleToggleAlert(true)}
                      disabled={!selectedVehicle}
                    >
                      Enable
                    </button>

                    <button
                      className="disable-button-Alerts"
                      onClick={() => handleToggleAlert(false)}
                      disabled={!selectedVehicle}
                    >
                      Disable
                    </button>
                  </>
                )}
              </div>

              {selectedConfigAlert !== "panic_button" && (
                <div
                  className="current-status-Alerts"
                  style={{
                    marginTop: "15px",
                    padding: "10px",
                    background: "#f5f5f5",
                    borderRadius: "5px",
                  }}
                >
                  <p style={{ margin: "5px 0" }}>
                    Current Status :{" "}
                    <strong
                      style={{ color: alertEnabled ? "#4CAF50" : "#F44336" }}
                    >
                      {alertEnabled ? "Enabled" : "Disabled"}
                    </strong>
                  </p>

                  {showInput && (
                    <p style={{ margin: "5px 0" }}>
                      Current Threshold Value :{" "}
                      <strong>{getSavedThreshold()}</strong>
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="config-select-group-Alerts">
          <label htmlFor="customer-select-Alerts">Choose Customer *</label>
          <select
            id="customer-select-Alerts"
            className="config-select-Alerts"
            value={selectedCustomer}
            onChange={(e) => setSelectedCustomer(e.target.value)}
            disabled={customers.length === 0}
          >
            {customers.length > 0 ? (
              customers.map((customer) => (
                <option key={customer.CustomerID} value={customer.CustomerID}>
                  {customer.CustomerName}
                </option>
              ))
            ) : (
              <option value="">No Customers Available</option>
            )}
          </select>
        </div>

        <div className="config-select-group-Alerts">
          <label htmlFor="vehicle-select-Alerts">Choose Vehicle *</label>
          <select
            id="vehicle-select-Alerts"
            className="config-select-Alerts"
            value={selectedVehicle}
            onChange={(e) => setSelectedVehicle(e.target.value)}
            disabled={vehicles.length === 0 || !selectedCustomer}
          >
            {vehicles.length > 0 ? (
              vehicles.map((vehicle) => (
                <option key={vehicle.VehicleID} value={vehicle.VehicleID}>
                  {vehicle.VehicleID}
                </option>
              ))
            ) : (
              <option value="">No Vehicles Available</option>
            )}
          </select>
        </div>
      </div>

      {selectedConfigAlert === "custom_alerts" && (
        <div className="config-custom-settings-Alerts">
          <h3 className="config-section-title-Alerts">Analog & Digital I/O</h3>
          <div className="config-controls-Alerts">
            <div className="config-select-group-Alerts">
              <label htmlFor="analog-input-1-Alerts">
                Analog Input 1 (Status:{" "}
                {analogInput1Enabled ? "Enabled" : "Disabled"})
              </label>
              <select
                id="analog-input-1-Alerts"
                className="config-select-Alerts"
                value={analogInput1Threshold}
                onChange={(e) => setAnalogInput1Threshold(e.target.value)}
                disabled={!selectedVehicle}
              >
                {analogOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}%
                  </option>
                ))}
              </select>
              <div className="button-container-Alerts single-line">
                <button
                  className="enable-button-Alerts"
                  onClick={() => handleSaveConfiguration(true, 1)}
                  disabled={!selectedVehicle}
                >
                  Enable
                </button>
                <button
                  className="disable-button-Alerts"
                  onClick={() => handleSaveConfiguration(false, 1)}
                  disabled={!selectedVehicle}
                >
                  Disable
                </button>
              </div>
            </div>

            <div className="config-select-group-Alerts">
              <label htmlFor="analog-input-2-Alerts">
                Analog Input 2 (Status:{" "}
                {analogInput2Enabled ? "Enabled" : "Disabled"})
              </label>
              <select
                id="analog-input-2-Alerts"
                className="config-select-Alerts"
                value={analogInput2Threshold}
                onChange={(e) => setAnalogInput2Threshold(e.target.value)}
                disabled={!selectedVehicle}
              >
                {analogOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}%
                  </option>
                ))}
              </select>
              <div className="button-container-Alerts single-line">
                <button
                  className="enable-button-Alerts"
                  onClick={() => handleSaveConfiguration(true, 2)}
                  disabled={!selectedVehicle}
                >
                  Enable
                </button>
                <button
                  className="disable-button-Alerts"
                  onClick={() => handleSaveConfiguration(false, 2)}
                  disabled={!selectedVehicle}
                >
                  Disable
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// --- Main Component ---
export default function Alerts() {
  const [currentView, setCurrentView] = useState("dashboard");
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [selectedOption, setSelectedOption] = useState("Today");
  const [vehicleIdQuery, setVehicleIdQuery] = useState("ALL");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const vehicleListQuery = useVehicleListQuery();
  const alertCountsQuery = useAlertCountsQuery(
    vehicleIdQuery,
    selectedOption,
    fromDate,
    toDate,
  );
  const [showDatePopup, setShowDatePopup] = useState(false);

  const {
    data: apiCounts = {},
    isLoading: loadingCounts,
    isFetching: fetchingCounts,
  } = alertCountsQuery;
  const isQueryActive = loadingCounts || fetchingCounts;

  const alertDataWithCounts = useMemo(() => {
    return initialAlertData.map((alert) => {
      let count = apiCounts[alert.label] ?? alert.count;

      if (alert.label === "Custom Alerts") {
        count =
          (apiCounts["Analog 1 High"] || 0) + (apiCounts["Analog 2 High"] || 0);
      }

      return {
        ...alert,
        count,
        status: count > 0 ? "active" : "inactive",
      };
    });
  }, [apiCounts]);

  const handleApplyCustomDate = () => {
    if (!fromDate || !toDate) {
      alert("Please select both dates");
      return;
    }
    setSelectedOption("Custom");
    setShowDatePopup(false);
  };
  const handleSelectAlert = (alert) => {
    if (!vehicleIdQuery) {
      setSelectedAlert(null);
      return;
    }
    setSelectedAlert(alert);
  };
  const handleCloseDetails = () => setSelectedAlert(null);
  const handleOpenConfig = () => {
    setCurrentView("configurations");
    setSelectedAlert(null);
  };
  const handleCloseConfig = () => setCurrentView("dashboard");

  return (
    <div className="Alerts-dashboard">
      {currentView === "dashboard" && (
        <div className="Alerts-main-view">
          <DashboardView
            alertData={alertDataWithCounts}
            onSelectAlert={handleSelectAlert}
            onOpenConfig={handleOpenConfig}
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOption}
            searchQuery={vehicleIdQuery}
            setSearchQuery={setVehicleIdQuery}
            loadingCounts={isQueryActive}
            vehicleListQuery={vehicleListQuery}
            fromDate={fromDate}
            toDate={toDate}
            setFromDate={setFromDate}
            setToDate={setToDate}
            showDatePopup={showDatePopup}
            setShowDatePopup={setShowDatePopup}
            handleApplyCustomDate={handleApplyCustomDate}
          />
          {selectedAlert && (
            <DetailsPanel
              alert={selectedAlert}
              vehicleId={vehicleIdQuery}
              range={selectedOption}
              fromDate={fromDate}
              toDate={toDate}
              onClose={handleCloseDetails}
            />
          )}
        </div>
      )}
      {currentView === "configurations" && (
        <ConfigurationView
          onClose={handleCloseConfig}
          alertOptions={CREATE_ALERT_OPTIONS}
        />
      )}
    </div>
  );
}
